import { Vector3 } from 'three';
import {
  EntryFrame,
  TemplateResult,
  straight,
  bankedTurn,
  helix,
  airtimeHill,
  sBend,
  verticalLoop,
  corkscrew,
  fromSamples,
} from './templates';

/** World grid cell size — default snap / RCT piece spacing. */
export const RCT_CELL = 5;

export type RctPieceId =
  | 'straight'
  | 'straightLong'
  | 'gentleUp'
  | 'gentleDown'
  | 'steepUp'
  | 'steepDown'
  | 'curveL45'
  | 'curveR45'
  | 'curveL90'
  | 'curveR90'
  | 'bankedL90'
  | 'bankedR90'
  | 'sBendL'
  | 'sBendR'
  | 'airtime'
  | 'helixDown'
  | 'loop'
  | 'corkscrew';

export type RctPieceGroup = 'flat' | 'slope' | 'curve' | 'special';

export interface RctPieceDef {
  id: RctPieceId;
  label: string;
  short: string;
  group: RctPieceGroup;
  hint: string;
  build(entry: EntryFrame): TemplateResult;
}

/** Snap a world point onto the RCT grid in XZ; Y left alone (or pass through snapHeight). */
export function snapToGrid(p: Vector3, cell = RCT_CELL): Vector3 {
  return new Vector3(Math.round(p.x / cell) * cell, p.y, Math.round(p.z / cell) * cell);
}

/** Snap height to half-cell steps (gentle slopes land cleanly). */
export function snapHeight(y: number, step = RCT_CELL / 2): number {
  return Math.round(y / step) * step;
}

/**
 * Quantize the plan heading to the nearest 45° (RCT cardinal/diagonal) while
 * preserving pitch — slope pieces must keep their climb/descent angle at the tip.
 */
export function snapHeading(tan: Vector3): Vector3 {
  const hx = tan.x;
  const hz = tan.z;
  const horizLen = Math.hypot(hx, hz);
  if (horizLen < 1e-8) {
    // nearly vertical — keep as-is
    const v = tan.clone();
    if (v.lengthSq() < 1e-8) return new Vector3(1, 0, 0);
    return v.normalize();
  }
  const angle = Math.atan2(hz, hx);
  const step = Math.PI / 4;
  const snapped = Math.round(angle / step) * step;
  // rebuild with the same pitch (y / horiz) so gentle-up exits stay angled
  return new Vector3(Math.cos(snapped) * horizLen, tan.y, Math.sin(snapped) * horizLen).normalize();
}

/** Entry frame at the free end: plan heading snapped to 45°, pitch preserved. */
export function rctEntryFrom(pos: Vector3, tan: Vector3, up: Vector3): EntryFrame {
  const snapped = snapHeading(tan);
  const u = up.clone().addScaledVector(snapped, -up.dot(snapped));
  if (u.lengthSq() < 1e-6) u.set(0, 1, 0).addScaledVector(snapped, -snapped.y);
  if (u.lengthSq() < 1e-6) u.set(0, 1, 0);
  u.normalize();
  return { pos: pos.clone(), tan: snapped, up: u };
}

/**
 * Constant-grade slope: linear rise so the exit tangent stays pitched (RCT-style).
 * Easing to flat at the end was wrong — the next piece should leave the tip climbing.
 */
function slope(
  entry: EntryFrame,
  length: number,
  deltaY: number,
): TemplateResult {
  const { pos, tan } = entry;
  const fwdH = new Vector3(tan.x, 0, tan.z);
  if (fwdH.lengthSq() < 1e-8) fwdH.set(1, 0, 0);
  fwdH.normalize();
  const n = Math.max(12, Math.round(length / 0.5));
  const points: Vector3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    points.push(pos.clone().addScaledVector(fwdH, length * t).add(new Vector3(0, deltaY * t, 0)));
  }
  return fromSamples(points, entry, Math.max(2, Math.round(n / 4)));
}

export const RCT_PIECES: RctPieceDef[] = [
  {
    id: 'straight',
    label: 'Straight',
    short: '—',
    group: 'flat',
    hint: 'Flat straight — 1 cell',
    build: (e) => straight(e, { length: RCT_CELL }),
  },
  {
    id: 'straightLong',
    label: 'Straight ×2',
    short: '══',
    group: 'flat',
    hint: 'Flat straight — 2 cells',
    build: (e) => straight(e, { length: RCT_CELL * 2 }),
  },
  {
    id: 'gentleUp',
    label: 'Gentle up',
    short: '╱',
    group: 'slope',
    hint: 'Gentle climb — +½ cell over 2 cells',
    build: (e) => slope(e, RCT_CELL * 2, RCT_CELL / 2),
  },
  {
    id: 'gentleDown',
    label: 'Gentle down',
    short: '╲',
    group: 'slope',
    hint: 'Gentle drop — −½ cell over 2 cells',
    build: (e) => slope(e, RCT_CELL * 2, -RCT_CELL / 2),
  },
  {
    id: 'steepUp',
    label: 'Steep up',
    short: '⟋',
    group: 'slope',
    hint: 'Steep climb — +1 cell over 2 cells',
    build: (e) => slope(e, RCT_CELL * 2, RCT_CELL),
  },
  {
    id: 'steepDown',
    label: 'Steep down',
    short: '⟍',
    group: 'slope',
    hint: 'Steep drop — −1 cell over 2 cells',
    build: (e) => slope(e, RCT_CELL * 2, -RCT_CELL),
  },
  {
    id: 'curveL45',
    label: 'Left 45°',
    short: '↰',
    group: 'curve',
    hint: 'Flat left turn — 45°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 2, angleDeg: 45, bankDeg: 0, dir: -1, easeFrac: 0.15 }),
  },
  {
    id: 'curveR45',
    label: 'Right 45°',
    short: '↱',
    group: 'curve',
    hint: 'Flat right turn — 45°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 2, angleDeg: 45, bankDeg: 0, dir: 1, easeFrac: 0.15 }),
  },
  {
    id: 'curveL90',
    label: 'Left 90°',
    short: '↺',
    group: 'curve',
    hint: 'Flat left turn — 90°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 2, angleDeg: 90, bankDeg: 0, dir: -1, easeFrac: 0.12 }),
  },
  {
    id: 'curveR90',
    label: 'Right 90°',
    short: '↻',
    group: 'curve',
    hint: 'Flat right turn — 90°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 2, angleDeg: 90, bankDeg: 0, dir: 1, easeFrac: 0.12 }),
  },
  {
    id: 'bankedL90',
    label: 'Banked L',
    short: '⟲',
    group: 'curve',
    hint: 'Banked left turn — 90°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 3, angleDeg: 90, bankDeg: 40, dir: -1 }),
  },
  {
    id: 'bankedR90',
    label: 'Banked R',
    short: '⟳',
    group: 'curve',
    hint: 'Banked right turn — 90°',
    build: (e) => bankedTurn(e, { radius: RCT_CELL * 3, angleDeg: 90, bankDeg: 40, dir: 1 }),
  },
  {
    id: 'sBendL',
    label: 'S-bend L',
    short: '⋍',
    group: 'curve',
    hint: 'S-bend offset left — 1 cell',
    build: (e) => sBend(e, { length: RCT_CELL * 3, offset: -RCT_CELL }),
  },
  {
    id: 'sBendR',
    label: 'S-bend R',
    short: '≂',
    group: 'curve',
    hint: 'S-bend offset right — 1 cell',
    build: (e) => sBend(e, { length: RCT_CELL * 3, offset: RCT_CELL }),
  },
  {
    id: 'airtime',
    label: 'Airtime hill',
    short: '⌢',
    group: 'special',
    hint: 'Parabolic airtime hill',
    build: (e) => airtimeHill(e, { length: RCT_CELL * 4, height: RCT_CELL }),
  },
  {
    id: 'helixDown',
    label: 'Helix down',
    short: '🌀',
    group: 'special',
    hint: 'One-turn descending helix',
    build: (e) => helix(e, { radius: RCT_CELL * 2, turns: 1, deltaHeight: -RCT_CELL, dir: 1, bankDeg: 45 }),
  },
  {
    id: 'loop',
    label: 'Vertical loop',
    short: '◯',
    group: 'special',
    hint: 'Clothoid vertical loop',
    build: (e) => verticalLoop(e, { height: RCT_CELL * 4 }),
  },
  {
    id: 'corkscrew',
    label: 'Corkscrew',
    short: '➰',
    group: 'special',
    hint: 'Single corkscrew',
    build: (e) => corkscrew(e, { radius: RCT_CELL * 1.1, length: RCT_CELL * 6 }),
  },
];

export const RCT_PIECE_BY_ID: Record<RctPieceId, RctPieceDef> = Object.fromEntries(
  RCT_PIECES.map((p) => [p.id, p]),
) as Record<RctPieceId, RctPieceDef>;

export const RCT_GROUPS: { id: RctPieceGroup; label: string }[] = [
  { id: 'flat', label: 'Flat' },
  { id: 'slope', label: 'Slopes' },
  { id: 'curve', label: 'Curves' },
  { id: 'special', label: 'Special' },
];

/** Sample points along a built piece for the ghost preview line. */
export function samplePiecePath(result: TemplateResult, step = 0.75): Vector3[] {
  const pts: Vector3[] = [];
  const nodes = result.nodes;
  if (nodes.length === 0) return pts;
  // dense enough polyline from Bézier samples via chord approximation
  for (let i = 0; i < nodes.length - 1; i++) {
    const a = nodes[i];
    const b = nodes[i + 1];
    const p0 = a.p;
    const p1 = a.p.clone().add(a.hOut);
    const p2 = b.p.clone().add(b.hIn);
    const p3 = b.p;
    const chord = p0.distanceTo(p3);
    const n = Math.max(4, Math.round(chord / step));
    for (let k = 0; k < n; k++) {
      const t = k / n;
      pts.push(bezierPoint(p0, p1, p2, p3, t));
    }
  }
  pts.push(nodes[nodes.length - 1].p.clone());
  return pts;
}

function bezierPoint(p0: Vector3, p1: Vector3, p2: Vector3, p3: Vector3, t: number): Vector3 {
  const u = 1 - t;
  const uu = u * u;
  const tt = t * t;
  return p0
    .clone()
    .multiplyScalar(uu * u)
    .addScaledVector(p1, 3 * uu * t)
    .addScaledVector(p2, 3 * u * tt)
    .addScaledVector(p3, tt * t);
}
